import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '../../utils/cn';

// Extracted as a top-level const so consumers (Storybook stories,
// contrast tests, downstream apps) can iterate the variant key space
// exhaustively without maintaining a parallel hardcoded list. cva's
// return type does NOT expose `.variants` at runtime, so we keep the
// schema here as the single source of truth and pass it through to cva
// below. Each new variant added here is automatically picked up by
// `BUTTON_VARIANT_KEYS` and any consumer iterating it.
export const BUTTON_VARIANT_CLASSES = {
  // Literal fallbacks mirror tokens.css exactly (600=#2563EB, 700=#1D4ED8,
  // 800=#1E40AF) so an unresolved brand token degrades to the correct brand
  // blue instead of transparent (white-on-white). Hardening against any
  // cascade-edge surface that mounts this primitive without @spark/ui/styles.css.
  primary:
    'bg-[var(--color-brand-primary-600,#2563EB)] text-white shadow-xs hover:bg-[var(--color-brand-primary-700,#1D4ED8)] active:bg-[var(--color-brand-primary-800,#1E40AF)]',
  secondary:
    'bg-white text-text-strong border border-border shadow-xs hover:bg-surface-muted hover:border-border-strong active:bg-border',
  outline:
    'bg-white text-brand-700 border border-brand-300 shadow-xs hover:bg-brand-50 hover:border-brand-400 active:bg-brand-100',
  ghost:
    'bg-transparent text-brand-700 hover:bg-brand-50 active:bg-brand-100',
  // SPARK-526 - `ghost` on a dark surface (e.g. the admin Sidebar
  // dark-tone footer) renders brand-blue on near-black, which fails
  // WCAG AA. This variant uses the dark-surface foreground tokens
  // the Sidebar itself uses for its nav items: `text-white/80` muted
  // default to `text-white` on hover, translucent white hover/active
  // fills, and a transparent focus-ring offset so the brand-300 ring
  // reads cleanly on the dark gradient. White on the sidebar footer
  // (#0A1628) is ~16:1, well above 4.5:1. The `!` modifier on the
  // ring-offset override defeats the base-class `ring-offset-white`
  // since both target the same CSS property and Tailwind's source
  // order would otherwise be undefined.
  'ghost-on-dark':
    'bg-transparent text-white/80 hover:text-white hover:bg-white/10 active:text-white active:bg-white/15 focus-visible:!ring-offset-transparent',
  // Dark-surface secondary — the bordered translucent control the learner
  // lesson player uses on its charcoal panels (walkthrough Play 3 follow-up:
  // the primitives must speak the dark language or the bespoke zones
  // persist). Foreground/border come from the #3629 dark-surface tokens.
  'secondary-on-dark':
    'bg-white/5 text-text-on-dark border border-border-on-dark hover:bg-white/10 hover:text-white active:bg-white/15 focus-visible:!ring-offset-transparent',
  // The learner CTA treatment: vertical brand gradient (vs the flat admin
  // primary). Shared by lesson-player forward actions and delight CTAs;
  // usually paired with `pill`.
  'primary-gradient':
    'bg-gradient-to-b from-[var(--color-brand-primary-600,#2563EB)] to-[var(--color-brand-primary-700,#1D4ED8)] text-white shadow-xs hover:from-[var(--color-brand-primary-700,#1D4ED8)] hover:to-[var(--color-brand-primary-800,#1E40AF)] active:brightness-95',
  destructive:
    'bg-danger text-white shadow-xs hover:brightness-105 active:brightness-95',
} as const;

// `Object.keys` widens to `string[]`; the cast preserves the literal
// union so callers can pass entries straight back to the `variant` prop.
export const BUTTON_VARIANT_KEYS = Object.keys(
  BUTTON_VARIANT_CLASSES,
) as Array<keyof typeof BUTTON_VARIANT_CLASSES>;

export const buttonStyles = cva(
  [
    'inline-flex items-center justify-center gap-2 font-semibold whitespace-nowrap select-none',
    'transition-all duration-fast outline-none',
    'focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2 focus-visible:ring-offset-white',
    'active:scale-[0.98]',
    'disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100',
  ].join(' '),
  {
    variants: {
      variant: BUTTON_VARIANT_CLASSES,
      size: {
        // TODO(SPARK-628): `sm` IconButton (h-7 = 32px in SPARK tokens,
        // 28px in Tailwind default) misses the D-072 44x44 mobile hit
        // target on touch surfaces. SPARK-619 scoped the fix to `md`
        // only since that's the call site driving notification-feed UI;
        // `sm` IconButton accessibility on touch needs a follow-up
        // ticket that decides between (a) a wrapping span giving
        // every IconButton a 44x44 hit area regardless of visible size,
        // or (b) restricting `sm` to non-touch surfaces only with a
        // lint rule.
        // SPARK design-system default radius is rounded-xl across every size.
        // This is the canonical accent-button shape the redesign settled on
        // (matching the "Start learning" / "Go to home" / "Continue" CTAs in
        // the redesign prototypes). IconButton (iconOnly + aspect-square)
        // becomes a rounded square rather than a circle, which is expected.
        // Heights/padding are unchanged so `md` stays the comfortable standard
        // and buttons never read oversized.
        sm: 'h-7 px-3 text-body-sm rounded-xl',
        md: 'h-9 px-4 text-body-sm rounded-xl',
        lg: 'h-11 px-5 text-body rounded-xl',
      },
      iconOnly: {
        true: 'aspect-square px-0',
        false: '',
      },
      fullWidth: { true: 'w-full', false: '' },
      // Fully-rounded shape for the learner surfaces' pill controls (pager,
      // header chips, CTAs). `!` because the size classes carry rounded-xl
      // and cn is plain clsx: without important, stylesheet order (not
      // class order) would decide which radius wins.
      pill: { true: '!rounded-full', false: '' },
    },
    // SPARK-619 - IconButton `md` delivers a >=44x44 hit target per
    // D.2.1.UI §8.2.A and D-072. The visible button is `h-9 w-9` via
    // `aspect-square`; a `::before` pseudo-element ring at `inset-[-4px]`
    // extends the clickable area 4px on every side. `relative` anchors
    // the absolutely-positioned pseudo to the button itself. The
    // compound variant scopes the ring strictly to `iconOnly: true +
    // size: 'md'` so `sm` and `lg` stay untouched (see the lg-absence
    // guard in Button.test.tsx). The SPARK design system overrides
    // Tailwind's spacing scale (`--space-9` = 48px), so the visible
    // button renders at 48x48 and the `::before` ring extends the click
    // area to 56x56. D-072 is satisfied on the visible button alone in
    // that configuration; the ring is defensive headroom that also
    // covers configurations where the spacing scale isn't overridden.
    // Consumers placing two `md` IconButtons adjacent should use a
    // `gap-2` (8px) minimum to keep the 4px rings from overlapping.
    // Current callsites (Sidebar, NotificationBell, FeedItem dismiss)
    // all use `gap-1` (4px) which is at the edge of acceptable; `gap-0`
    // would overlap and ambiguate which button receives a click.
    // Empirical proof of the ring's interactivity lives in
    // `apps/admin/e2e/icon-button-hit-target.spec.ts`.
    compoundVariants: [
      {
        iconOnly: true,
        size: 'md',
        class: "relative before:absolute before:inset-[-4px] before:content-['']",
      },
    ],
    defaultVariants: { variant: 'primary', size: 'md', iconOnly: false, fullWidth: false, pill: false },
  },
);

// Exported so consumers (Storybook, contrast tests, downstream apps)
// can reference the cva variant union without re-deriving it. Excludes
// `null | undefined` since cva represents "unspecified" with those and
// consumers iterating the variant space want concrete keys only.
export type ButtonVariant = NonNullable<VariantProps<typeof buttonStyles>['variant']>;

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonStyles> {
  iconLeading?: ReactNode;
  iconTrailing?: ReactNode;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    iconOnly,
    fullWidth,
    pill,
    iconLeading,
    iconTrailing,
    loading,
    disabled,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;
  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      // className-merge caveat: `cn` is plain clsx (no tailwind-merge), so a
      // consumer `className` does NOT override a base utility that targets the
      // same CSS property. Both land in the class list and Tailwind's source
      // order decides the winner. To reliably override base layout or visuals
      // (e.g. the base `justify-center` when a left-aligned row is wanted), use
      // an inline `style` (wins over classes) or a `!` important modifier (see
      // the ghost-on-dark ring-offset above). A bare utility class is not enough.
      className={cn(buttonStyles({ variant, size, iconOnly, fullWidth, pill }), className)}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-[spark-spin_0.7s_linear_infinite]" aria-hidden /> : iconLeading}
      {!iconOnly && children}
      {iconTrailing && !loading ? iconTrailing : null}
    </button>
  );
});
